import React from 'react';
import { Box } from '@mui/material';
import { MarkdownText } from './MarkdownText';
import { McpBadge } from './McpBadge';
import type { McpToolMetadata } from '../../contexts/PlannerContext';

interface MessageWithMcpBadgesProps {
  text: string;
  mcpMetadata?: McpToolMetadata[];
  variant?: 'body1' | 'body2';
  color?: string;
  sx?: any;
  fontWeight?: number;
}

const MCP_BADGE_PATTERN = /🔘\s+(\w+)\s+mcp\s+used#(\d+)/g;

export const MessageWithMcpBadges: React.FC<MessageWithMcpBadgesProps> = ({
  text,
  mcpMetadata,
  variant = 'body2',
  color = 'inherit',
  sx,
  fontWeight,
}) => {
  // If no metadata, just render plain markdown
  if (!mcpMetadata || mcpMetadata.length === 0) {
    return (
      <MarkdownText variant={variant} color={color} sx={{ fontWeight, ...sx }}>
        {text}
      </MarkdownText>
    );
  }

  // Parse the text and create components
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(MCP_BADGE_PATTERN);

  while ((match = regex.exec(text)) !== null) {
    const fullMatch = match[0];
    const source = match[1];
    const index = parseInt(match[2], 10);
    const matchIndex = match.index;

    // Add text before the badge
    if (matchIndex > lastIndex) {
      const textBefore = text.substring(lastIndex, matchIndex);
      parts.push(
        <MarkdownText
          key={`text-${lastIndex}`}
          variant={variant}
          color={color}
          sx={{ fontWeight, display: 'inline' }}
        >
          {textBefore}
        </MarkdownText>
      );
    }

    // Add the badge component
    if (mcpMetadata[index]) {
      parts.push(
        <McpBadge
          key={`badge-${index}`}
          source={source}
          metadata={mcpMetadata[index]}
          index={index}
        />
      );
    }

    lastIndex = matchIndex + fullMatch.length;
  }

  // Add remaining text after the last badge
  if (lastIndex < text.length) {
    const textAfter = text.substring(lastIndex);
    parts.push(
      <MarkdownText
        key={`text-${lastIndex}`}
        variant={variant}
        color={color}
        sx={{ fontWeight, display: 'inline' }}
      >
        {textAfter}
      </MarkdownText>
    );
  }

  // If no badges were found in text but we have metadata, append all badges at the end
  // This handles cases where the LLM doesn't generate the citation markers
  if (parts.length <= 1 && mcpMetadata.length > 0 && !text.match(MCP_BADGE_PATTERN)) {
    mcpMetadata.forEach((meta, idx) => {
      parts.push(
        <Box key={`auto-badge-${idx}`} sx={{ display: 'inline-block', ml: 1, mt: 1 }}>
          <McpBadge
            source={meta.tool_name.split('_')[0]} // extract 'search' from 'search_tool' etc
            metadata={meta}
            index={idx}
          />
        </Box>
      );
    });
  }

  return (
    <Box
      sx={{
        display: 'inline',
        alignItems: 'center',
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      {parts}
    </Box>
  );
};
